import { Box, Typography } from "@mui/material";
import {
  Children,
  isValidElement,
  ReactElement,
  ReactNode,
  useLayoutEffect,
  useRef,
} from "react";
import { optionsMenuStyleProps } from "../theme/main";
import { OptionsCategoryProps } from "./OptionsCategory";

const CATEGORY_EXPAND_TRANSITION_MS = 250;

type OptionsMenuProps = {
  title?: string;
  subTitle?: string;
  activeChildId?: string;
  children?: ReactNode;
};

export function OptionsMenu({
  title,
  subTitle,
  activeChildId,
  children,
}: OptionsMenuProps) {
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  const childRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const {
    titleChildrenSpace,
    titleStyle,
    subTitleStyle,
    childrenSpace,
    ...menuStyle
  } = optionsMenuStyleProps;
  const childElements = Children.toArray(children).filter(
    isValidElement,
  ) as ReactElement<OptionsCategoryProps>[];

  useLayoutEffect(() => {
    if (!activeChildId) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const scrollBox = scrollBoxRef.current;
      const child = childRefs.current[activeChildId];

      if (!scrollBox || !child) {
        return;
      }

      scrollBox.scrollTo({
        top: child.offsetTop,
        behavior: "smooth",
      });
    }, CATEGORY_EXPAND_TRANSITION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [activeChildId]);

  return (
    <Box sx={{ ...menuStyle, display: "flex", flexDirection: "column" }}>
      {title && <Typography sx={titleStyle}>{title}</Typography>}
      {subTitle && <Typography sx={subTitleStyle}>{subTitle}</Typography>}

      <Box sx={titleChildrenSpace} />
      <Box
        ref={scrollBoxRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: "hidden",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: childrenSpace.space,
            position: "relative",
          }}
        >
          {childElements.map((child, index) => (
            <Box
              key={child.props.id || index}
              sx={{ position: "relative" }}
              ref={(element: HTMLDivElement | null) => {
                if (child.props.id) {
                  childRefs.current[child.props.id] = element;
                }
              }}
            >
              {child}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
